import React, { useRef, useEffect, useState } from "react"
import Button from "components/Form/Button"
import { IoClose } from "react-icons/io5"
import { twMerge } from "tailwind-merge"
import { useLang } from "hooks/useLang"

interface IProps {
    isOpen: boolean
    onClose: () => void
    title?: string
    children?: React.ReactNode
    className?: string
    showDefaultHeader?: boolean
    ignoredRefs?: React.RefObject<any>[]
    style?: any
}

const Sidebar = ({
    isOpen,
    onClose,
    title,
    children,
    className: customClassName,
    showDefaultHeader = true, 
    ignoredRefs = [],
    style,
}: IProps) => {
    const sidebarRef = useRef<HTMLDivElement>(null)
    const { isAr } = useLang()
    const [isVisible, setIsVisible] = useState(isOpen)

    // 🚫 Prevent background scroll (desktop + mobile)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(target) &&
                !ignoredRefs.some(
                    (ref) => ref.current && ref.current.contains(target)
                )
            ) {
                onClose()
            }
        }

        const preventTouchScroll = (e: TouchEvent) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(e.target as Node)
            ) {
                e.preventDefault()
            }
        }

        if (isOpen) {
            setIsVisible(true)
            document.addEventListener("mousedown", handleClickOutside)
            document.addEventListener("touchmove", preventTouchScroll, {
                passive: false,
            })
            document.body.style.overflow = "hidden"
            document.documentElement.style.overflow = "hidden"
        } else {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("touchmove", preventTouchScroll)
            document.body.style.overflow = ""
            document.documentElement.style.overflow = ""
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("touchmove", preventTouchScroll)
            document.body.style.overflow = ""
            document.documentElement.style.overflow = ""
        }
    }, [isOpen, onClose, ignoredRefs])

    
    return (
        <menu>
            {/* Blur background */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <div
                ref={sidebarRef}
                style={{ ...style }}
                className={twMerge(
                    `fixed top-0 ${isAr ? "left-0" : "right-0"} 
					h-screen w-[40vw] max-w-[400px] z-40 transform
					bg-dark_white shadow-lg transition-transform duration-300 ease-out
					overflow-y-auto 
					${isOpen
                        ? "translate-x-0"
                        : isAr
                            ? "-translate-x-full"
                            : "translate-x-full"
                    }`,
                    customClassName
                )}
            >
                <div className="relative min-h-full w-full px-4 py-8 text-[#201750]">
                    {showDefaultHeader ? (
                        <div className="flex flex-col gap-y-7 h-full">
                            <div className="flex">
                                <Button
                                    className="bg-transparent hover:bg-transparent"
                                    onClick={onClose}
                                >
                                    <IoClose className="text-2xl text-[#201750]" />
                                </Button>
                            </div>
                            {title && (
                                <h2 className="font-bold text-2xl capitalize">
                                    {title}
                                </h2>
                            )}
                            <div>{children}</div>
                        </div>
                    ) : (
                        <div className="h-full">{children}</div>
                    )}
                </div>
            </div>
        </menu>
    )
}

export default Sidebar
